import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import type { ElementConfig } from '../ui-element.type';
import type { UIOptionData } from './ui-option.types';
import { UIOption } from './ui-option';
import styles from './ui-option-account.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIOption}
 * @alias UIOptionAccount
 * @element ui-option-account
 * @classdesc Represents a class for <code>ui-option-account</code> element.
 * Similar to ui-option, but holds account and owner. Used with
 * <code>ui-dropdown layout="account"</code> and <code>ui-option-list</code>.
 * @property {boolean} optgroup {@attr optgroup}
 * @property {boolean} control Flag to distinguish the location where option will be shown.
 * @property {string} [accountnumber] {@attr accountnumber} Account number
 * @property {string} [accountid] {@attr accountid} Bank account id (ibank, local, etc.).
 * @property {string} [holder] {@attr holder} Name of the account holder/owner.
 * @property {string} [amount] {@attr amount} Amount of available money in the account.
 * @property {string} [currency="EUR"] {@attr currency} Currency of account.
 * be located in control of the dropdown.
 * @slot
 * @example
 * <ui-dropdown layout="account">
 *  <select>
 *   <option data-holder="Tiina Puu" data-amount="100" data-account-number="EE111149490"></option>
 *   <option data-holder="Mari Muul" data-amount="200" data-account-number="EE222249450"></option>
 *  </select>
 * </ui-dropdown>
 * @example
 * <ui-option-list label="Select account">
 *   <ui-option-account optgroup text="Personal"></ui-option-account>
 *   <ui-option-account holder="Tiina Puu" amount="100" accountnumber="EE111149490" accountid="123"></ui-option-account>
 *   <ui-option-account holder="Mari Muul" amount="560" currency="SEK" accountnumber="EE222249490" accountid="456" selected></ui-option-account>
 * </ui-option-list>
 */

@Component({
    tag: 'ui-option-account',
    styles: styles,
})
export class UIOptionAccount extends UIOption {
    @Attribute(String) currency: string = 'EUR';
    @Attribute(String) accountid?: string;
    @Attribute(String) amount?: string;
    @Attribute(String) holder?: string;
    @Attribute(String) accountnumber?: string;

    /**
     * @param {HTMLOptionElement} option
     * @param {string} text
     * @returns {boolean}
     */
    findMatch(option: HTMLOptionElement, text: string): boolean {
        const strings = [
            option.innerText,
            option.getAttribute('value'),
            option.getAttribute('data-holder'),
            option.getAttribute('data-account-number'),
        ];
        return (
            strings.filter((str) => {
                return str?.toLowerCase().includes(text.toLowerCase());
            }).length > 0
        );
    }

    /**
     * Gets object as data from this option.
     * @returns {UIOptionData}
     */
    getData(): UIOptionData {
        return {
            id: this.accountid,
            currency: this.currency,
            holder: this.holder,
            amount: this.amount,
            accountnumber: this.accountnumber,
        };
    }

    getValue(): string | undefined {
        return this.accountnumber;
    }

    getText(): string | undefined {
        return this.holder;
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.insertElements(
            (this.optgroup
                ? [this.buildOptGroupLabel()]
                : [
                      {
                          tagName: 'span',
                          classList: {
                              'ui-option-account__holder': true,
                              'ui-option__label': true,
                              '-full': !this.amount,
                          },
                          children: [this.holder ?? ''],
                      },
                      this.amount
                          ? {
                                tagName: 'ui-amount',
                                attributes: {
                                    currency: this.currency,
                                    value: this.amount,
                                },
                            }
                          : undefined,
                      {
                          tagName: 'span',
                          attributes: {
                              class: 'ui-option-account__number',
                          },
                          children: [this.accountnumber ?? ''],
                      },
                  ].filter(Boolean)) as Array<
                HTMLElement | DocumentFragment | ElementConfig
            >
        );

        super.render();
    }
}
